import { useEffect } from 'react'
import { NavLink, Outlet, useNavigate, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../shared/store/auth.store.js'
import { useOrgStore } from '../shared/store/org.store.js'
import { ROUTES } from './routes.js'
import type { PlatformOutletContext } from './platform-context.js'

interface NavItem {
  to: string
  label: string
  end: boolean
}

export default function PlatformLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { platformRole } = useOutletContext<PlatformOutletContext>()
  const user = useAuthStore(s => s.user)
  const logout = useAuthStore(s => s.logout)

  const organizations = useOrgStore(s => s.organizations)
  const orgStatus = useOrgStore(s => s.status)
  const loadOrganizations = useOrgStore(s => s.load)

  useEffect(() => {
    if (orgStatus === 'idle') void loadOrganizations()
  }, [orgStatus, loadOrganizations])

  const navItems: NavItem[] = [
    { to: ROUTES.PLATFORM, label: t('platform.nav.overview'), end: true },
    { to: ROUTES.PLATFORM_ORGANIZATIONS, label: t('platform.nav.organizations'), end: false },
    { to: ROUTES.PLATFORM_CITIES, label: t('platform.nav.cities'), end: false },
    { to: ROUTES.PLATFORM_ADMINISTRATORS, label: t('platform.nav.administrators'), end: false },
  ]

  const displayName = user?.firstName || user?.name || user?.email || ''
  const hasWorkspace = organizations.length > 0

  async function handleLogout() {
    await logout()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  return (
    <div className="app-layout platform-layout">
      <header className="app-header platform-header">
        <div className="app-header-left">
          <span className="app-logo">Magic Food Platform</span>
          <span className="platform-role-badge">
            {t(`platform.role.${platformRole}`)}
          </span>
        </div>
        <div className="app-header-right">
          {hasWorkspace && (
            <NavLink className="btn btn-ghost" to={ROUTES.DASHBOARD}>
              {t('platform.backToWorkspace')}
            </NavLink>
          )}
          <span className="app-user">{displayName}</span>
          <button type="button" className="btn btn-ghost" onClick={() => void handleLogout()}>
            {t('app.logout')}
          </button>
        </div>
      </header>

      <div className="app-body">
        <nav className="app-sidebar" aria-label={t('platform.nav.label')}>
          <div className="sidebar-section-label">{t('platform.workspace')}</div>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                isActive ? 'sidebar-link sidebar-link--active' : 'sidebar-link'}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="app-main">
          <Outlet context={{ platformRole } satisfies PlatformOutletContext} />
        </main>
      </div>
    </div>
  )
}
